import { useEffect, useRef } from "react";
import { GOOGLE_CLIENT_ID } from "@/lib/google-config";
import { loadGoogleIdentity } from "@/lib/google-signin";

type Props = {
  /** Called with the Google-signed ID token (credential JWT) on success. */
  onCredential: (credential: string) => void;
  disabled?: boolean;
};

/**
 * Renders Google's official "Continue with Google" button (GIS renderButton).
 * The button is a Google-owned iframe, so it can't inherit our theme tokens —
 * it uses the standard Google styling, which users recognize. On click Google
 * opens its popup and returns an ID token via the initialize callback.
 */
export function GoogleSignInButton({ onCredential, disabled }: Props) {
  const holderRef = useRef<HTMLDivElement>(null);
  // Keep the latest callback without re-running the effect (which would
  // re-render the Google button and drop its internal state).
  const cbRef = useRef(onCredential);
  cbRef.current = onCredential;

  useEffect(() => {
    let cancelled = false;
    loadGoogleIdentity()
      .then(() => {
        if (cancelled || !holderRef.current) return;
        const g = (window as any).google;
        if (!g?.accounts?.id) return;
        g.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          ux_mode: "popup",
          callback: (resp: { credential?: string }) => {
            if (resp?.credential) cbRef.current(resp.credential);
          },
        });
        holderRef.current.innerHTML = "";
        g.accounts.id.renderButton(holderRef.current, {
          type: "standard",
          theme: "outline",
          size: "large",
          text: "continue_with",
          shape: "rectangular",
          logo_alignment: "center",
          width: 300,
        });
      })
      .catch(() => {
        /* GIS failed to load — button just won't appear; email/password still work. */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div
      className={disabled ? "pointer-events-none opacity-50" : ""}
      aria-hidden={disabled}
    >
      {/* colorScheme:light keeps Google's button legible on our dark theme */}
      <div ref={holderRef} className="flex justify-center" style={{ colorScheme: "light" }} />
    </div>
  );
}
